import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { DRILLDOWN_DIALOG_ID } from '../global-constants';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';
import { stripHtmlToText } from '../utils/navItemUtils';
import useTrackEvent from '../hooks/useTrackEvent';
import {
	DRAWER_EVENT,
	NAV_DEPTH,
	TRACKING_CONFIG,
	WIDGET_STATE
} from '../utils/trackEvent';
import NavItemLabel from './NavItemLabel';

/**
 * SubMenuTrigger — a nav item button that opens a sub-menu panel (drilldown mode).
 *
 * @param {Object} props
 * @param {NavigationItem} props.item - Navigation item data
 * @param {(item: NavigationItem, triggerRef?: React.RefObject) => void} props.onSubmenuOpen - Called with the item when triggered
 * @param {boolean} props.isActive - Whether this item or a descendant is active
 * @param {boolean} [props.isLeafActive] - Whether a direct child page is the current URL
 */
const SubMenuTrigger = ({ item, onSubmenuOpen, isActive, isLeafActive }) => {
	const triggerButtonRef = useRef(null);
	const { activeDrillDownItem } = useDrawerNavigation();
	const track = useTrackEvent();
	const activeClass = isActive ? 'ws-nav-drawer__nav-item--active' : '';
	const leafActiveClass = isLeafActive
		? 'ws-nav-drawer__nav-item--leaf-active'
		: '';

	// Only used to gate the drilldown-open tracking below (CR-5 fires on the way
	// in, not on a re-click while already open) — no longer feeds aria-expanded,
	// which was removed as unobservable (see US2059853 T61).
	const isThisItemOpen =
		activeDrillDownItem !== null &&
		(item.id !== undefined
			? item.id === activeDrillDownItem.id
			: item.label === activeDrillDownItem.label);

	const handleClick = () => {
		// Only fires on the way in. Coming back out is a drilldown close, not a
		// drill-down, and is handled by the back button / swipe path. CR-5 is
		// explicitly about *opening* a sub-navigation panel, unlike CR-4's
		// every-click featured item, so the guard is correct here.
		if (!isThisItemOpen) {
			// Labels can carry CMS markup — NavItemLabel sanitizes and renders it as
			// HTML. Analytics wants the plain text the user actually read, or the raw
			// tags would land in `element`, which the legacy GA path turns into
			// `eventName`.
			const label = stripHtmlToText(item.label);

			track(DRAWER_EVENT.SUBNAV_DRILLDOWN, {
				element: TRACKING_CONFIG.getDrilldownEl(label),
				elementCTA: label,
				result: TRACKING_CONFIG.subNavOpenedResult,
				widgetState: WIDGET_STATE.DRAWER_OPEN,
				label,
				targetDepth: NAV_DEPTH.SUB_NAV
			});
		}
		onSubmenuOpen(item, triggerButtonRef);
	};

	return (
		<button
			type="button"
			ref={triggerButtonRef}
			aria-controls={DRILLDOWN_DIALOG_ID}
			aria-haspopup="dialog"
			className={setClassNames([
				'ws-nav-drawer__nav-item',
				'ws-nav-drawer__nav-item--drilldown',
				'py-4',
				'px-0',
				'd-flex',
				'align-items-center',
				'justify-content-between',
				'w-100',
				'bg-transparent',
				'text-left',
				activeClass,
				leafActiveClass
			])}
			onClick={handleClick}
		>
			<NavItemLabel label={item.label} className="h4" />
			<i
				className={setClassNames([
					'ddc-icon',
					'ddc-icon-skinny-arrow-right',
					'ws-nav-drawer__nav-item-icon',
					'ddc-font-size-large',
					'd-flex',
					'flex-shrink-0',
					'align-items-center',
					'justify-content-center'
				])}
				aria-hidden="true"
			/>
		</button>
	);
};

SubMenuTrigger.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		selected: PropTypes.bool,
		matched: PropTypes.bool
	}).isRequired,
	onSubmenuOpen: PropTypes.func.isRequired,
	isActive: PropTypes.bool,
	isLeafActive: PropTypes.bool
};

export default SubMenuTrigger;
